import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { supabase } from '../lib/supabase'
import { SPORTS_TZ } from '../lib/sportsDay'

function getUserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || SPORTS_TZ }
  catch { return SPORTS_TZ }
}

export function useMyLeagues() {
  return useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get(`/leagues?tz=${encodeURIComponent(getUserTz())}`),
  })
}

export function useReorderLeagues() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (order) => api.patch('/leagues/reorder', { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leagues'] }),
  })
}

export function useMyLeagueWins() {
  return useQuery({
    queryKey: ['leagues', 'my-wins'],
    queryFn: () => api.get('/leagues/my-wins'),
  })
}

export function useLeague(id) {
  return useQuery({
    queryKey: ['leagues', id],
    queryFn: () => api.get(`/leagues/${id}`),
    enabled: !!id,
    refetchInterval: 60000,
  })
}

export function useLeagueReport(leagueId) {
  return useQuery({
    queryKey: ['league-report', leagueId],
    queryFn: () => api.get(`/leagues/${leagueId}/report`),
    enabled: !!leagueId,
    staleTime: Infinity,
    retry: false,
  })
}

export function useLeagueMembers(id) {
  return useQuery({
    queryKey: ['leagues', id, 'members'],
    queryFn: () => api.get(`/leagues/${id}/members`),
    enabled: !!id,
  })
}

export function useLeagueStandings(id) {
  return useQuery({
    queryKey: ['leagues', id, 'standings'],
    queryFn: () => api.get(`/leagues/${id}/standings`),
    enabled: !!id,
  })
}

export function useLeagueWeeks(id) {
  return useQuery({
    queryKey: ['leagues', id, 'weeks'],
    queryFn: () => api.get(`/leagues/${id}/weeks`),
    enabled: !!id,
  })
}

export function useCreateLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data) => api.post('/leagues', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useJoinLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, inviteCode }) =>
      api.post(`/leagues/${leagueId || '_'}/join`, { invite_code: inviteCode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useLeagueBackdrops(sport) {
  return useQuery({
    queryKey: ['league-backdrops', sport],
    queryFn: () => api.get(`/leagues/backdrops${sport ? `?sport=${sport}` : ''}`),
  })
}

export function useOpenLeagues() {
  return useQuery({
    queryKey: ['leagues', 'open'],
    queryFn: () => api.get('/leagues/open'),
  })
}

export function useRequestInvite() {
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/request-invite`),
  })
}

export function useJoinOpenLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/join-open`),
    onSuccess: (_, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', 'open'] })
      // Flip the league detail view from preview → member immediately.
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] })
      // Drop any pending invitation card from the navbar — joinLeague
      // cleans up the row server-side, but the client cache needs a nudge.
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useUpdateLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, ...data }) => api.patch(`/leagues/${leagueId}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useToggleAutoConnect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, autoConnect }) =>
      api.patch(`/leagues/${leagueId}/auto-connect`, { auto_connect: autoConnect }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId] })
    },
  })
}

export function useDeleteLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.delete(`/leagues/${leagueId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useCompleteLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/complete`),
    onSuccess: (_, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useLeaveLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, userId }) => api.delete(`/leagues/${leagueId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

// Pick'em selections
export function usePickemSelections(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'pickem', 'selections'],
    queryFn: () => api.get(`/leagues/${leagueId}/pickem/selections`),
    enabled: !!leagueId,
  })
}

export function useSelectPickemGames() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, weekId, gameIds }) =>
      api.post(`/leagues/${leagueId}/pickem/selections`, { week_id: weekId, game_ids: gameIds }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'pickem'] })
    },
  })
}

// League Picks (new pick'em system)
export function useLeaguePicks(leagueId, weekId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'pickem', 'picks', weekId],
    queryFn: () => api.get(`/leagues/${leagueId}/pickem/picks${weekId ? `?week_id=${weekId}` : ''}`),
    enabled: !!leagueId,
  })
}

export function useLeagueGames(leagueId, weekId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'pickem', 'games', weekId],
    queryFn: () => api.get(`/leagues/${leagueId}/pickem/games?week_id=${weekId}`),
    enabled: !!leagueId && !!weekId,
  })
}

export function useSubmitLeaguePick() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, weekId, gameId, pickedTeam }) =>
      api.post(`/leagues/${leagueId}/pickem/picks`, {
        week_id: weekId,
        game_id: gameId,
        picked_team: pickedTeam,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'pickem'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'standings'] })
    },
  })
}

export function useUserLeaguePicks(leagueId, userId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'pickem', 'member-picks', userId],
    queryFn: () => api.get(`/leagues/${leagueId}/pickem/member-picks/${userId}`),
    enabled: !!leagueId && !!userId,
  })
}

export function useDeleteLeaguePick() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, gameId }) =>
      api.delete(`/leagues/${leagueId}/pickem/picks/${gameId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'pickem'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'standings'] })
    },
  })
}

// Survivor
export function useSurvivorBoard(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'survivor', 'board'],
    queryFn: () => api.get(`/leagues/${leagueId}/survivor/board`),
    enabled: !!leagueId,
  })
}

export function useUsedTeams(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'survivor', 'used-teams'],
    queryFn: () => api.get(`/leagues/${leagueId}/survivor/used-teams`),
    enabled: !!leagueId,
  })
}

export function useSubmitSurvivorPick() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, weekId, gameId, pickedTeam }) =>
      api.post(`/leagues/${leagueId}/survivor/picks`, {
        week_id: weekId,
        game_id: gameId,
        picked_team: pickedTeam,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'survivor'] })
    },
  })
}

export function useSubmitTouchdownPick() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, weekId, playerId }) =>
      api.post(`/leagues/${leagueId}/survivor/touchdown-pick`, { week_id: weekId, player_id: playerId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'survivor'] })
    },
  })
}

export function useTouchdownPlayers(leagueId, position, query) {
  const params = new URLSearchParams()
  if (position && position !== 'All') params.set('position', position)
  if (query) params.set('q', query)
  return useQuery({
    queryKey: ['leagues', leagueId, 'survivor', 'touchdown-players', position, query],
    queryFn: () => api.get(`/leagues/${leagueId}/survivor/touchdown-players?${params}`),
    enabled: !!leagueId,
  })
}

export function useSettleSurvivorLeague() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/survivor/settle`),
    onSuccess: (_, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'survivor'] })
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useDeleteSurvivorPick() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, weekId }) =>
      api.delete(`/leagues/${leagueId}/survivor/picks/${weekId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'survivor'] })
    },
  })
}

// Squares
export function useSquaresBoard(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'squares', 'board'],
    queryFn: () => api.get(`/leagues/${leagueId}/squares/board`),
    enabled: !!leagueId,
    refetchInterval: (query) => {
      const board = query.state.data
      if (!board) return 5000
      if (!board.digits_locked) return 2000
      // Poll faster during live game for quarter score updates
      if (board.games?.status === 'live') return 10000
      if (board.digits_locked && board.games?.starts_at && new Date(board.games.starts_at) <= new Date() && board.games?.status !== 'final') return 10000
      return 30000
    },
  })
}

export function useClaimSquare() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, rowPos, colPos }) =>
      api.post(`/leagues/${leagueId}/squares/claim`, { row_pos: rowPos, col_pos: colPos }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'squares'] })
    },
  })
}

export function useUnclaimSquare() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, rowPos, colPos }) =>
      api.post(`/leagues/${leagueId}/squares/unclaim`, { row_pos: rowPos, col_pos: colPos }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'squares'] })
    },
  })
}

export function useRandomAssignSquares() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/squares/random-assign`),
    onSuccess: (_, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'squares'] })
    },
  })
}

export function useLockDigits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/squares/lock-digits`),
    onSuccess: (_, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'squares'] })
    },
  })
}

export function useScoreQuarter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, quarter, awayScore, homeScore }) =>
      api.post(`/leagues/${leagueId}/squares/score-quarter`, {
        quarter,
        away_score: awayScore,
        home_score: homeScore,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'squares'] })
    },
  })
}

export function useUpdateBoardSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, ...settings }) =>
      api.patch(`/leagues/${leagueId}/squares/board`, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'squares'] })
    },
  })
}

// Bracket
export function useBracketTournament(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'tournament'],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/tournament`),
    enabled: !!leagueId,
  })
}

export function useUpdateBracketTournament() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, ...data }) =>
      api.patch(`/leagues/${leagueId}/bracket/tournament`, data),
    onSuccess: (_, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'bracket', 'tournament'] })
    },
  })
}

export function useBracketEntry(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'entry'],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/entry`),
    enabled: !!leagueId,
    retry: 3,
    staleTime: 30000,
  })
}

export function useBracketEntries(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'entries'],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/entries`),
    enabled: !!leagueId,
  })
}

export function useViewBracketEntry(leagueId, userId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'entries', userId],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/entries/${userId}`),
    enabled: !!leagueId && !!userId,
  })
}

export function useSeriesGames(leagueId, team1, team2) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'series-games', team1, team2],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/series-games?team1=${encodeURIComponent(team1)}&team2=${encodeURIComponent(team2)}`),
    enabled: !!leagueId && !!team1 && !!team2,
  })
}

export function useMyOtherBracketEntries(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'bracket', 'my-other-entries'],
    queryFn: () => api.get(`/leagues/${leagueId}/bracket/my-other-entries`),
    enabled: !!leagueId,
  })
}

export function useSubmitBracket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, picks, entryName, tiebreakerScore }) =>
      api.post(`/leagues/${leagueId}/bracket/entry`, { picks, entry_name: entryName, tiebreaker_score: tiebreakerScore }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', variables.leagueId, 'bracket'] })
    },
  })
}

export function useBracketTemplatesActive(sport) {
  return useQuery({
    queryKey: ['bracketTemplates', 'active', sport],
    queryFn: () => api.get(`/leagues/bracket-templates/active${sport ? `?sport=${sport}` : ''}`),
  })
}

// ── League Thread ──

export function useLeagueThread(leagueId) {
  return useInfiniteQuery({
    queryKey: ['leagues', leagueId, 'thread'],
    queryFn: ({ pageParam }) =>
      api.get(`/leagues/${leagueId}/thread${pageParam ? `?before=${pageParam}` : ''}`),
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: !!leagueId,
  })
}

export function useSendThreadMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, content, user_tags, image_urls }) =>
      api.post(`/leagues/${leagueId}/thread`, { content, user_tags, image_urls }),
    onSuccess: (_data, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'thread'] })
    },
  })
}

export function useRealtimeLeagueThread(leagueId) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!leagueId) return
    const channel = supabase
      .channel(`league-thread-${leagueId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'league_messages',
        filter: `league_id=eq.${leagueId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'thread'] })
      })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [leagueId, queryClient])
}

export function useThreadUnread(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'thread', 'unread'],
    queryFn: () => api.get(`/leagues/${leagueId}/thread/unread`),
    enabled: !!leagueId,
    staleTime: 30_000,
  })
}

export function useMarkThreadRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/thread/read`),
    onSuccess: (_data, leagueId) => {
      queryClient.setQueryData(['leagues', leagueId, 'thread', 'unread'], { unread: false })
    },
  })
}

// ── Fantasy Football ──

export function useFantasySettings(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'settings'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/settings`),
    enabled: !!leagueId,
    // draft_status lives here, and it drives the entire paused / live / on-the-
    // clock UI — yet this was the one draft query that never polled. The board
    // polls every 5s but that is a different key, and useRealtimeDraft
    // invalidates 'draft' and 'players' and not 'settings', so a pause only
    // surfaced on a window-focus refetch. Observed 2026-09-09: a commissioner
    // did not see his own league paused while another manager did, because
    // that manager happened to tab back in.
    refetchInterval: 10000,
  })
}

export function useUpdateFantasySettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, ...patch }) => api.patch(`/leagues/${leagueId}/fantasy/settings`, patch),
    onSuccess: (_d, { leagueId }) => {
      // Invalidate the whole 'fantasy' subtree, not just 'settings'.
      // invalidateQueries matches by key PREFIX, so the old
      // [...,'fantasy','settings'] key never matched [...,'fantasy','underfill']
      // — changing num_teams left the underfill banner rendering the state
      // from before the change, telling a resolved 6-team league it was
      // underfilled and offering to "drop the most recent 0 signups".
      // Derived queries keep getting added under this prefix, so scoping to
      // the parent is what keeps the next one from silently going stale too.
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useDraftBoard(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'draft'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/draft`),
    enabled: !!leagueId,
    refetchInterval: 5000, // Poll during draft
  })
}

export function useFantasyRoster(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'roster'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/roster`),
    enabled: !!leagueId,
    // 20s on the My Team page so live point totals on each player feel
    // current during games. React Query stops polling when the tab is
    // hidden so this isn't wasteful in the background.
    refetchInterval: 20000,
  })
}

export function useFantasyLineupHistory(leagueId, week, season) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'lineupHistory', week, season],
    queryFn: () => api.get(`/dfs/lineup-history?league_id=${leagueId}&week=${week}&season=${season}`),
    enabled: !!leagueId && !!week,
  })
}

// Per-week projections map for the My Team weekly navigation. Returns
// { [player_id]: projection } so the client can overlay the right
// projection on whichever displayRoster source is active for the
// viewed week.
export function useFantasyWeekProjections(leagueId, week) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'weekProjections', week],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/projections/week/${week}`),
    enabled: !!leagueId && !!week,
    staleTime: 5 * 60 * 1000, // projections shift slowly intra-day
  })
}

export function useSetFantasyLineup(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (slots) => api.post(`/leagues/${leagueId}/fantasy/lineup`, { slots }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster'] })
    },
  })
}

export function useFantasyWeeklyLineup(leagueId, week) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'weeklyLineup', week],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/lineup/week/${week}`),
    enabled: !!leagueId && !!week,
  })
}

export function useSetFantasyWeeklyLineup(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ week, slots }) =>
      api.post(`/leagues/${leagueId}/fantasy/lineup/week/${week}`, { slots }),
    onSuccess: (_d, { week }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'weeklyLineup', week] })
    },
  })
}

/**
 * Clear healed players off IR, dropping one roster player when the manager is
 * at the cap. Called with no argument to ASK — the server answers 400 with
 * needs_drop so the caller can open the drop picker — then again with the
 * chosen player id to commit.
 */
export function useResolveIneligibleIr(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dropPlayerId = null) =>
      api.post(`/leagues/${leagueId}/fantasy/resolve-ir`, { drop_player_id: dropPlayerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'transactions'] })
    },
  })
}

export function useDropRosterPlayer(leagueId) {
  const queryClient = useQueryClient()
  const rosterKey = ['leagues', leagueId, 'fantasy', 'roster']
  return useMutation({
    mutationFn: (playerId) => api.delete(`/leagues/${leagueId}/fantasy/roster/${playerId}`),
    // Optimistic: remove the dropped row from the cached roster immediately
    // so the UI updates the moment the user confirms.
    onMutate: async (playerId) => {
      await queryClient.cancelQueries({ queryKey: rosterKey })
      const previous = queryClient.getQueryData(rosterKey)
      if (Array.isArray(previous)) {
        queryClient.setQueryData(rosterKey, previous.filter((r) => r.player_id !== playerId))
      }
      return { previous }
    },
    onError: (_err, _playerId, ctx) => {
      // Roll back if the server rejected the drop
      if (ctx?.previous) queryClient.setQueryData(rosterKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: rosterKey })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'players'] })
    },
  })
}

export function useAddDropPlayer(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ addPlayerId, dropPlayerId }) =>
      api.post(`/leagues/${leagueId}/fantasy/add-drop`, { add_player_id: addPlayerId, drop_player_id: dropPlayerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'players'] })
    },
  })
}

export function useFantasyTrades(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'trades'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/trades`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

export function useProposeTrade(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload) => api.post(`/leagues/${leagueId}/fantasy/trades`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'trades'] }),
  })
}

export function useRespondToTrade(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ tradeId, action, drop_player_ids, reason }) => {
      const body = {}
      if (drop_player_ids?.length) body.drop_player_ids = drop_player_ids
      if (reason) body.reason = reason
      return api.post(`/leagues/${leagueId}/fantasy/trades/${tradeId}/${action}`, Object.keys(body).length ? body : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'trades'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster'] })
    },
  })
}

export function useWaiverState(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'waivers', 'state'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/waivers/state`),
    enabled: !!leagueId,
  })
}

export function useMyWaiverClaims(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'waivers', 'claims'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/waivers/claims`),
    enabled: !!leagueId,
  })
}

export function useSubmitWaiverClaim(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload) => api.post(`/leagues/${leagueId}/fantasy/waivers/claims`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'waivers'] })
    },
  })
}

export function useCancelWaiverClaim(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (claimId) => api.delete(`/leagues/${leagueId}/fantasy/waivers/claims/${claimId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'waivers'] })
    },
  })
}

// Reorder the caller's own pending claims. Takes the FULL list of claim ids,
// most-wanted first — the server rejects a partial list, because omitted
// claims would keep stale ranks that collide with the new ones.
export function useReorderWaiverClaims(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (claimIds) =>
      api.patch(`/leagues/${leagueId}/fantasy/waivers/claims/order`, { claim_ids: claimIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'waivers'] })
    },
  })
}

export function usePlayerDetail(leagueId, playerId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'player-detail', playerId],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/players/${playerId}/detail`),
    enabled: !!leagueId && !!playerId,
    refetchInterval: 15000,
  })
}

export function useAvailablePlayers(leagueId, query, position, sort) {
  const params = new URLSearchParams()
  if (query) params.set('q', query)
  if (position) params.set('position', position)
  if (sort) params.set('sort', sort)
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'players', query, position, sort],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/players?${params}`),
    enabled: !!leagueId,
    staleTime: 10_000,
    refetchInterval: 120000,
  })
}

export function useInitDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/init`),
    onSuccess: (_data, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useReorderDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, order }) => api.post(`/leagues/${leagueId}/fantasy/draft/reorder`, { order }),
    onSuccess: (_data, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useStartDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/start`),
    onSuccess: (_data, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useMakeDraftPick() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, playerId }) => api.post(`/leagues/${leagueId}/fantasy/draft/pick`, { playerId }),
    onSuccess: (_data, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useMakeOfflineDraftPick() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, playerId }) => api.post(`/leagues/${leagueId}/fantasy/draft/offline-pick`, { playerId }),
    onSuccess: (_data, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useStartOfflineDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/start-offline`),
    onSuccess: (_data, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useUndoDraftPick() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/undo`),
    onSuccess: (_data, leagueId) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function usePauseDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/pause`),
    onSuccess: (_d, leagueId) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] }),
  })
}

export function useResumeDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/resume`),
    onSuccess: (_d, leagueId) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] }),
  })
}

export function useSetAutoDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, userId, enabled }) => api.post(`/leagues/${leagueId}/fantasy/draft/autodraft`, { userId, enabled }),
    onSuccess: (_d, { leagueId }) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] }),
  })
}

export function useCancelAutoDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/draft/autodraft/cancel`),
    onSuccess: (_d, leagueId) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] }),
  })
}

export function useFantasyStandings(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'standings'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/standings`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

export function useDraftPlayerDetail(leagueId, playerId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'draft-player-detail', playerId],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/draft-player-detail/${playerId}`),
    enabled: !!leagueId && !!playerId,
  })
}

export function useMockDraftPlayerDetail(playerId, scoring) {
  return useQuery({
    queryKey: ['mock-draft', 'player-detail', playerId, scoring],
    queryFn: () => api.get(`/mock-draft/players/${playerId}/detail?scoring=${encodeURIComponent(scoring || 'ppr')}`),
    enabled: !!playerId,
  })
}

export function useGlobalRank(leagueId, enabled = true) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'globalRank'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/global-rank`),
    enabled: !!leagueId && enabled,
  })
}

export function useMyRankings(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'myRankings'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/my-rankings`),
    enabled: !!leagueId,
  })
}

export function useSetMyRankings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, playerIds }) => api.put(`/leagues/${leagueId}/fantasy/my-rankings`, { playerIds }),
    onSuccess: (_d, { leagueId }) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'myRankings'] }),
  })
}

export function useResetMyRankings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leagueId) => api.post(`/leagues/${leagueId}/fantasy/my-rankings/reset`),
    onSuccess: (_d, leagueId) => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'myRankings'] }),
  })
}

export function useDraftQueue(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'draftQueue'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/draft/queue`),
    enabled: !!leagueId,
  })
}

export function useSetDraftQueue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, playerIds }) => api.put(`/leagues/${leagueId}/fantasy/draft/queue`, { playerIds }),
    onSuccess: (_data, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'draftQueue'] })
    },
  })
}

export function useRealtimeDraft(leagueId, userId) {
  const queryClient = useQueryClient()
  const [presentUserIds, setPresentUserIds] = useState(() => new Set())

  useEffect(() => {
    if (!leagueId || !userId) return
    const channel = supabase
      .channel(`fantasy-draft-${leagueId}`, {
        config: { presence: { key: userId } },
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'fantasy_draft_picks',
        filter: `league_id=eq.${leagueId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'draft'] })
        queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'players'] })
        // 'settings' carries draft_status. Without this a pause or resume was
        // invisible to a client whose socket was healthy — the one piece of
        // state that most needs to propagate instantly was the one the
        // realtime handler ignored.
        queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'settings'] })
      })
      .on('presence', { event: 'sync' }, () => {
        setPresentUserIds(new Set(Object.keys(channel.presenceState())))
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({})
        }
      })

    return () => { supabase.removeChannel(channel) }
  }, [leagueId, userId, queryClient])

  return presentUserIds
}

// ============================================
// NBA DFS
// ============================================

// How often a player pool re-checks itself.
//
// Injury news does not arrive when a game TIPS, it arrives in the hour or so
// before it, when teams post inactives. The old rule only tightened once a
// game was already live, so the window where availability actually changes
// was the window polled most slowly — every 2 minutes.
//
// That is the delay Nick hit: Jimmy Butler sat in the available list long
// enough to be picked, and had moved to Out by the time he looked again. The
// server was right the whole time (refreshNBAInjuries runs every minute and
// matched ESPN exactly); the client simply had not asked.
//
// 90 minutes before tip is deliberately generous — NBA inactives are usually
// ~30-60 min out, but a 30s poll for an extra half hour costs almost nothing
// against picking a player who is already ruled out.
function poolRefetchInterval(query) {
  const data = query.state.data
  if (!Array.isArray(data) || !data.length) return 120_000
  if (data.some((p) => p.game_status === 'live')) return 25_000
  const now = Date.now()
  const tipSoon = data.some((p) => {
    if (!p.game_starts_at) return false
    const t = new Date(p.game_starts_at).getTime()
    return t > now && t - now < 90 * 60 * 1000
  })
  return tipSoon ? 30_000 : 120_000
}

export function useNbaDfsPlayers(date) {
  return useQuery({
    queryKey: ['nba-dfs', 'players', date],
    queryFn: () => api.get(`/nba-dfs/players?date=${date}`),
    enabled: !!date,
    refetchInterval: poolRefetchInterval,
  })
}

export function useNbaDfsRoster(leagueId, date, season = 2026) {
  return useQuery({
    queryKey: ['nba-dfs', leagueId, 'roster', date],
    queryFn: () => api.get(`/nba-dfs/roster?league_id=${leagueId}&date=${date}&season=${season}`),
    enabled: !!leagueId && !!date,
    // Smart polling: tight when any rostered player is live, slow otherwise
    refetchInterval: (query) => {
      const slots = query.state.data?.nba_dfs_roster_slots || query.state.data?.dfs_roster_slots || []
      const anyLive = Array.isArray(slots) && slots.some((s) => s.game_status === 'live')
      return anyLive ? 10000 : 60000
    },
  })
}

export function useSaveNbaDfsRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/nba-dfs/roster', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nba-dfs', variables.league_id, 'roster'] })
    },
  })
}

export function useNbaDfsLive(leagueId, date, season = 2026) {
  return useQuery({
    queryKey: ['nba-dfs', leagueId, 'live', date],
    queryFn: () => api.get(`/nba-dfs/live?league_id=${leagueId}&date=${date}&season=${season}`),
    enabled: !!leagueId && !!date,
    // Smart polling: 5s during live action, 30s when nothing's running
    refetchInterval: (query) => {
      const data = query.state.data
      const anyLive = data?.rosters?.some?.((r) =>
        r.slots?.some?.((s) => s.game_status === 'live')
      )
      return anyLive ? 5000 : 30000
    },
  })
}

export function useNbaDfsPlayerLookup(name, sport) {
  const sportParam = sport ? `&sport=${encodeURIComponent(sport)}` : ''
  return useQuery({
    queryKey: ['nba-dfs', 'player-lookup', name, sport],
    queryFn: () => api.get(`/nba-dfs/player/lookup?name=${encodeURIComponent(name)}${sportParam}`),
    enabled: !!name,
    staleTime: 10 * 60 * 1000,
  })
}

export function useNbaDfsPlayerGamelog(espnId, sport = 'basketball_nba', idType = null, hint = null) {
  // idType disambiguates NFL ids: Sleeper and ESPN ids are both numeric
  // strings, so the server can't tell them apart on its own. `hint` carries
  // name/team so the server can still resolve players whose espn_id is null
  // (about half the active NFL roster).
  const qs = [
    `sport=${sport}`,
    idType ? `id_type=${idType}` : null,
    hint?.name ? `name=${encodeURIComponent(hint.name)}` : null,
    hint?.team ? `team=${encodeURIComponent(hint.team)}` : null,
  ].filter(Boolean).join('&')
  return useQuery({
    queryKey: ['nba-dfs', 'gamelog', espnId, sport, idType, hint?.name, hint?.team],
    queryFn: () => api.get(`/nba-dfs/player/${espnId}/gamelog?${qs}`),
    enabled: !!espnId,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  })
}

export function useNbaDfsStandings(leagueId) {
  return useQuery({
    queryKey: ['nba-dfs', leagueId, 'standings'],
    queryFn: () => api.get(`/nba-dfs/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// ============================================
// WNBA DFS
// ============================================

export function useWnbaDfsPlayers(date) {
  return useQuery({
    queryKey: ['wnba-dfs', 'players', date],
    queryFn: () => api.get(`/wnba-dfs/players?date=${date}`),
    enabled: !!date,
    refetchInterval: poolRefetchInterval,
  })
}

export function useWnbaDfsRoster(leagueId, date, season = 2026) {
  return useQuery({
    queryKey: ['wnba-dfs', leagueId, 'roster', date],
    queryFn: () => api.get(`/wnba-dfs/roster?league_id=${leagueId}&date=${date}&season=${season}`),
    enabled: !!leagueId && !!date,
    refetchInterval: (query) => {
      const slots = query.state.data?.wnba_dfs_roster_slots || []
      const anyLive = Array.isArray(slots) && slots.some((s) => s.game_status === 'live')
      return anyLive ? 10000 : 60000
    },
  })
}

export function useSaveWnbaDfsRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/wnba-dfs/roster', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wnba-dfs', variables.league_id, 'roster'] })
    },
  })
}

export function useWnbaDfsLive(leagueId, date, season = 2026) {
  return useQuery({
    queryKey: ['wnba-dfs', leagueId, 'live', date],
    queryFn: () => api.get(`/wnba-dfs/live?league_id=${leagueId}&date=${date}&season=${season}`),
    enabled: !!leagueId && !!date,
    refetchInterval: (query) => {
      const data = query.state.data
      const anyLive = data?.rosters?.some?.((r) =>
        r.slots?.some?.((s) => s.game_status === 'live')
      )
      return anyLive ? 5000 : 30000
    },
  })
}

export function useWnbaDfsStandings(leagueId) {
  return useQuery({
    queryKey: ['wnba-dfs', leagueId, 'standings'],
    queryFn: () => api.get(`/wnba-dfs/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// MLB DFS hooks
export function useMlbDfsPlayers(date) {
  return useQuery({
    queryKey: ['mlb-dfs', 'players', date],
    queryFn: () => api.get(`/mlb-dfs/players?date=${date}`),
    enabled: !!date,
    refetchInterval: poolRefetchInterval,
  })
}

export function useMlbDfsRoster(leagueId, date, season = 2026) {
  return useQuery({
    queryKey: ['mlb-dfs', leagueId, 'roster', date],
    queryFn: () => api.get(`/mlb-dfs/roster?league_id=${leagueId}&date=${date}&season=${season}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 60000,
  })
}

export function useSaveMlbDfsRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/mlb-dfs/roster', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mlb-dfs', variables.league_id, 'roster'] })
    },
  })
}

export function useMlbDfsLive(leagueId, date) {
  return useQuery({
    queryKey: ['mlb-dfs', leagueId, 'live', date],
    queryFn: () => api.get(`/mlb-dfs/live?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 30000,
  })
}

export function useNflDfsPlayers(week, season, position) {
  const params = new URLSearchParams({ week, season })
  if (position) params.set('position', position)
  return useQuery({
    queryKey: ['nfl-dfs', 'players', week, season, position],
    queryFn: () => api.get(`/dfs/players?${params}`),
    enabled: !!week,
    staleTime: 60000,
  })
}

export function useNflDfsRoster(leagueId, week, season) {
  return useQuery({
    queryKey: ['nfl-dfs', leagueId, 'roster', week, season],
    queryFn: () => api.get(`/dfs/roster?league_id=${leagueId}&week=${week}&season=${season}`),
    enabled: !!leagueId && !!week,
    refetchInterval: 60000,
  })
}

export function useSaveNflDfsRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/dfs/roster', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nfl-dfs', variables.league_id, 'roster'] })
    },
  })
}

export function useSubmitNflDfsRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/dfs/roster/submit', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nfl-dfs', variables.league_id, 'roster'] })
    },
  })
}

export function usePlayoffBracket(leagueId) {
  return useQuery({
    queryKey: ['playoff-bracket', leagueId],
    queryFn: () => api.get(`/dfs/playoff-bracket?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}

export function useNflDfsLive(leagueId, week, season, isHistorical = false) {
  return useQuery({
    queryKey: ['nfl-dfs', leagueId, 'live', week, season],
    queryFn: () => api.get(`/dfs/live?league_id=${leagueId}&week=${week}&season=${season}`),
    enabled: !!leagueId && !!week,
    // 5s during live games, 10s otherwise. People expect near-instant updates.
    // A finished week never changes, so browsing history doesn't poll at all.
    refetchInterval: (query) => {
      if (isHistorical) return false
      const data = query.state.data
      const anyLive = data?.any_live || data?.members?.some((m) => m.slots?.some((s) => s.game_status === 'live'))
      return anyLive ? 5000 : 10000
    },
  })
}

export function useFantasyMatchupLive(leagueId, week, season) {
  return useQuery({
    queryKey: ['fantasy', leagueId, 'matchup-live', week, season],
    queryFn: () => api.get(`/dfs/matchup-live?league_id=${leagueId}&week=${week}&season=${season}`),
    enabled: !!leagueId && !!week,
    refetchInterval: (query) => {
      const data = query.state.data
      const anyLive = data?.matchups?.some((m) =>
        m.home_roster?.some((s) => s.game_status === 'live') ||
        m.away_roster?.some((s) => s.game_status === 'live')
      )
      return anyLive ? 5000 : 10000
    },
  })
}

export function useBlurbPlayerIds(leagueId) {
  return useQuery({
    queryKey: ['fantasy', leagueId, 'blurb-ids'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/blurb-ids`),
    enabled: !!leagueId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Latest published note PLUS its text for each player on my roster — the
// Updates sheet on My Team. useBlurbPlayerIds covers the dots and is cached
// separately; this is only fetched when the sheet opens.
//
// Deliberately uncached. The pill's badge is derived live from the roster and
// blurbIds queries, so adding a player updated the count immediately while
// this list was still serving a 5-minute-old snapshot taken before the add:
// the new players were missing from the sheet, nothing could mark them seen,
// and the badge sat there refusing to clear.
//
// gcTime 0 as well as staleTime 0 — not belt and braces. The sheet snapshots
// unread state the first time data arrives, so being handed a cached array
// first would freeze that stale snapshot before the refetch landed. Dropping
// the cache on close means every open starts from no data and freezes on
// something fresh. It costs one request per open, and the query only runs
// while the sheet is actually open.
export function useFantasyRosterUpdates(leagueId, enabled = true) {
  return useQuery({
    queryKey: ['fantasy', leagueId, 'roster-updates'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/roster-updates`),
    enabled: !!leagueId && enabled,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useFantasyTransactions(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'transactions'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/transactions`),
    enabled: !!leagueId,
  })
}

export function useFantasyUserRoster(leagueId, userId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'roster', userId],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/roster/${userId}`),
    enabled: !!leagueId && !!userId,
  })
}

export function useFantasyMatchupWeek(leagueId, week, season, currentWeek) {
  return useQuery({
    queryKey: ['fantasy', leagueId, 'matchup-week', week, season],
    queryFn: () => api.get(`/dfs/matchup-week?league_id=${leagueId}&week=${week}&season=${season}&current_week=${currentWeek}`),
    enabled: !!leagueId && !!week && week !== currentWeek,
    staleTime: week < currentWeek ? Infinity : 60000, // past weeks never change
  })
}

export function useMlbDfsStandings(leagueId) {
  return useQuery({
    queryKey: ['mlb-dfs', leagueId, 'standings'],
    queryFn: () => api.get(`/mlb-dfs/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// HR Derby hooks
export function useHrDerbyPlayers(date) {
  return useQuery({
    queryKey: ['hr-derby', 'players', date],
    queryFn: () => api.get(`/hr-derby/players?date=${date}`),
    enabled: !!date,
  })
}

export function useHrDerbyPicks(leagueId, date) {
  return useQuery({
    queryKey: ['hr-derby', leagueId, 'picks', date],
    queryFn: () => api.get(`/hr-derby/picks?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 2 * 60 * 1000, // refresh every 2 min to pick up scored HRs
  })
}

export function useHrDerbyUsed(leagueId, date) {
  return useQuery({
    queryKey: ['hr-derby', leagueId, 'used', date],
    queryFn: () => api.get(`/hr-derby/used?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
  })
}

export function useSubmitHrDerbyPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/hr-derby/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr-derby', variables.league_id] })
    },
  })
}

export function useHrDerbyStandings(leagueId) {
  return useQuery({
    queryKey: ['hr-derby', leagueId, 'standings'],
    queryFn: () => api.get(`/hr-derby/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// Strikeouts Contest hooks (mirror HR Derby — pitchers + strikeouts)
export function useStrikeoutsPlayers(date) {
  return useQuery({
    queryKey: ['strikeouts', 'players', date],
    queryFn: () => api.get(`/strikeouts/players?date=${date}`),
    enabled: !!date,
  })
}

export function useStrikeoutsPicks(leagueId, date) {
  return useQuery({
    queryKey: ['strikeouts', leagueId, 'picks', date],
    queryFn: () => api.get(`/strikeouts/picks?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useStrikeoutsUsed(leagueId, date) {
  return useQuery({
    queryKey: ['strikeouts', leagueId, 'used', date],
    queryFn: () => api.get(`/strikeouts/used?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
  })
}

export function useSubmitStrikeoutsPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/strikeouts/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['strikeouts', variables.league_id] })
    },
  })
}

export function useStrikeoutsStandings(leagueId) {
  return useQuery({
    queryKey: ['strikeouts', leagueId, 'standings'],
    queryFn: () => api.get(`/strikeouts/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// 3-Point Contest hooks (mirror HR Derby exactly, NBA equivalent)
export function useThreePointPlayers(date) {
  return useQuery({
    queryKey: ['three-point', 'players', date],
    queryFn: () => api.get(`/three-point/players?date=${date}`),
    enabled: !!date,
    // Had no refetch at all — fetched once on mount and never asked again,
    // so an Out designation landing mid-session was never seen.
    refetchInterval: poolRefetchInterval,
  })
}

export function useThreePointPicks(leagueId, date) {
  return useQuery({
    queryKey: ['three-point', leagueId, 'picks', date],
    queryFn: () => api.get(`/three-point/picks?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useThreePointUsed(leagueId, date) {
  return useQuery({
    queryKey: ['three-point', leagueId, 'used', date],
    queryFn: () => api.get(`/three-point/used?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
  })
}

export function useSubmitThreePointPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/three-point/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['three-point', variables.league_id] })
    },
  })
}

export function useThreePointStandings(leagueId) {
  return useQuery({
    queryKey: ['three-point', leagueId, 'standings'],
    queryFn: () => api.get(`/three-point/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// WNBA 3-Point Contest hooks (mirror NBA version)
export function useWnbaThreePointPlayers(date) {
  return useQuery({
    queryKey: ['wnba-three-point', 'players', date],
    queryFn: () => api.get(`/wnba-three-point/players?date=${date}`),
    enabled: !!date,
    // Had no refetch at all — fetched once on mount and never asked again,
    // so an Out designation landing mid-session was never seen.
    refetchInterval: poolRefetchInterval,
  })
}

export function useWnbaThreePointPicks(leagueId, date) {
  return useQuery({
    queryKey: ['wnba-three-point', leagueId, 'picks', date],
    queryFn: () => api.get(`/wnba-three-point/picks?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useWnbaThreePointUsed(leagueId, date) {
  return useQuery({
    queryKey: ['wnba-three-point', leagueId, 'used', date],
    queryFn: () => api.get(`/wnba-three-point/used?league_id=${leagueId}&date=${date}`),
    enabled: !!leagueId && !!date,
  })
}

export function useSubmitWnbaThreePointPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/wnba-three-point/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wnba-three-point', variables.league_id] })
    },
  })
}

export function useWnbaThreePointStandings(leagueId) {
  return useQuery({
    queryKey: ['wnba-three-point', leagueId, 'standings'],
    queryFn: () => api.get(`/wnba-three-point/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// === Sacks Contest ===
export function useSacksPlayers() {
  return useQuery({
    queryKey: ['sacks', 'players'],
    queryFn: () => api.get('/sacks/players?locked=1'),
    staleTime: 60_000,
  })
}

export function useSacksPicks(leagueId) {
  return useQuery({
    queryKey: ['sacks', leagueId, 'picks'],
    queryFn: () => api.get(`/sacks/picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useSacksUsed(leagueId) {
  return useQuery({
    queryKey: ['sacks', leagueId, 'used'],
    queryFn: () => api.get(`/sacks/used?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}

export function useSubmitSacksPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/sacks/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sacks', variables.league_id] })
    },
  })
}

export function useSacksStandings(leagueId) {
  return useQuery({
    queryKey: ['sacks', leagueId, 'standings'],
    queryFn: () => api.get(`/sacks/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// === Interceptions Contest ===
export function useIntsPlayers() {
  return useQuery({
    queryKey: ['ints', 'players'],
    queryFn: () => api.get('/ints/players?locked=1'),
    staleTime: 60_000,
  })
}

export function useIntsPicks(leagueId) {
  return useQuery({
    queryKey: ['ints', leagueId, 'picks'],
    queryFn: () => api.get(`/ints/picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useIntsUsed(leagueId) {
  return useQuery({
    queryKey: ['ints', leagueId, 'used'],
    queryFn: () => api.get(`/ints/used?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}

export function useSubmitIntsPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/ints/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ints', variables.league_id] })
    },
  })
}

export function useIntsStandings(leagueId) {
  return useQuery({
    queryKey: ['ints', leagueId, 'standings'],
    queryFn: () => api.get(`/ints/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// === Tackles Contest ===
export function useTacklesPlayers() {
  return useQuery({
    queryKey: ['tackles', 'players'],
    queryFn: () => api.get('/tackles/players?locked=1'),
    staleTime: 60_000,
  })
}

export function useTacklesPicks(leagueId) {
  return useQuery({
    queryKey: ['tackles', leagueId, 'picks'],
    queryFn: () => api.get(`/tackles/picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useTacklesUsed(leagueId) {
  return useQuery({
    queryKey: ['tackles', leagueId, 'used'],
    queryFn: () => api.get(`/tackles/used?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}

export function useSubmitTacklesPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/tackles/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tackles', variables.league_id] })
    },
  })
}

export function useTacklesStandings(leagueId) {
  return useQuery({
    queryKey: ['tackles', leagueId, 'standings'],
    queryFn: () => api.get(`/tackles/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// === Receptions Contest ===
export function useReceptionsPlayers() {
  return useQuery({
    queryKey: ['receptions', 'players'],
    queryFn: () => api.get('/receptions/players?locked=1'),
    staleTime: 60_000,
  })
}

export function useReceptionsPicks(leagueId) {
  return useQuery({
    queryKey: ['receptions', leagueId, 'picks'],
    queryFn: () => api.get(`/receptions/picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 2 * 60 * 1000,
  })
}

export function useReceptionsUsed(leagueId) {
  return useQuery({
    queryKey: ['receptions', leagueId, 'used'],
    queryFn: () => api.get(`/receptions/used?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}

export function useSubmitReceptionsPicks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/receptions/picks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['receptions', variables.league_id] })
    },
  })
}

export function useReceptionsStandings(leagueId) {
  return useQuery({
    queryKey: ['receptions', leagueId, 'standings'],
    queryFn: () => api.get(`/receptions/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

// === TD Pass Competition ===
// Current NFL week + first-game kickoff for that week. Used by the
// create-league flow to render "Week N — kicks off DATE" instead of a
// generic "This Week" label. Offseason returns Week 1 + its opener.
export function useCurrentNflWeek() {
  return useQuery({
    queryKey: ['td-pass', 'current-week'],
    queryFn: () => api.get('/td-pass/current-week'),
    staleTime: 5 * 60_000,
  })
}

export function useTdPassQbs(leagueId) {
  return useQuery({
    queryKey: ['td-pass', leagueId, 'qbs'],
    queryFn: () => api.get(`/td-pass/qbs?league_id=${leagueId}`),
    enabled: !!leagueId,
    staleTime: 30_000,
  })
}

export function useTdPassMyPicks(leagueId) {
  return useQuery({
    queryKey: ['td-pass', leagueId, 'my-picks'],
    queryFn: () => api.get(`/td-pass/my-picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

export function useTdPassLeaguePicks(leagueId) {
  return useQuery({
    queryKey: ['td-pass', leagueId, 'league-picks'],
    queryFn: () => api.get(`/td-pass/league-picks?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

export function useTdPassStandings(leagueId) {
  return useQuery({
    queryKey: ['td-pass', leagueId, 'standings'],
    queryFn: () => api.get(`/td-pass/standings?league_id=${leagueId}`),
    enabled: !!leagueId,
    refetchInterval: 60000,
  })
}

export function useTdPassCurrentWeek() {
  return useQuery({
    queryKey: ['td-pass', 'current-week'],
    queryFn: () => api.get('/td-pass/current-week'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useFantasyUnderfillState(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'underfill'],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/underfill-state`),
    enabled: !!leagueId,
    // Members join from THEIR device, so nothing on this one invalidates
    // this query — with staleTime alone the "League is underfilled" banner
    // sat there claiming 5 of 6 had joined after the sixth was already in,
    // until the commissioner manually refreshed. Seen 2026-09-05 setting up
    // a test league.
    //
    // The endpoint is cheap (one settings row + a count) and this only
    // mounts on a traditional-fantasy league page, so a short poll is the
    // right trade for a banner that is otherwise confidently wrong at
    // exactly the moment it matters — while people are still joining.
    staleTime: 10_000,
    refetchInterval: 15_000,
  })
}

export function useResizeFantasyLeague(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/leagues/${leagueId}/fantasy/resize`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function useCancelFantasyLeague(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/leagues/${leagueId}/fantasy/cancel`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

export function usePostponeFantasyDraft(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (newDate) => api.post(`/leagues/${leagueId}/fantasy/postpone-draft`, { draft_date: newDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
    },
  })
}

// Renew a completed fantasy league into a fresh next-season league.
// See server/src/services/fantasyService.js renewLeague for the copy /
// reset rules. Returns { league: {id, name, invite_code, season_ordinal,
// parent_league_id}, alreadyRenewed: bool }.
export function useRenewFantasyLeague(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitedUserIds) =>
      api.post(`/leagues/${leagueId}/fantasy/renew`, { invitedUserIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'history'] })
    },
  })
}

// Full lineage (ancestors + current + descendants) for the messages
// history view.
export function useLeagueHistory(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'history'],
    queryFn: () => api.get(`/leagues/${leagueId}/history`),
    enabled: !!leagueId,
  })
}

export function useLineageSeasonStandings(currentLeagueId, seasonLeagueId, enabled = true) {
  return useQuery({
    queryKey: ['leagues', currentLeagueId, 'history', seasonLeagueId, 'standings'],
    queryFn: () => api.get(`/leagues/${currentLeagueId}/history/${seasonLeagueId}/standings`),
    enabled: enabled && !!currentLeagueId && !!seasonLeagueId,
  })
}

export function useNflSeasonOpener() {
  return useQuery({
    queryKey: ['td-pass', 'season-opener'],
    queryFn: () => api.get('/td-pass/season-opener'),
    staleTime: 60 * 60 * 1000,
  })
}

export function useSubmitTdPassPick() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leagueId, qbPlayerId }) =>
      api.post('/td-pass/picks', { league_id: leagueId, qb_player_id: qbPlayerId }),
    onSuccess: (_, { leagueId }) => {
      queryClient.invalidateQueries({ queryKey: ['td-pass', leagueId] })
    },
  })
}

export function useSurveyStatus(leagueId) {
  return useQuery({
    queryKey: ['survey-status', leagueId],
    queryFn: () => api.get(`/surveys/status?league_id=${leagueId}`),
    enabled: !!leagueId,
    staleTime: 60 * 1000,
  })
}

export function useReportProblem(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ message }) => api.post(`/leagues/${leagueId}/report-problem`, { message }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'report-problem'] }),
  })
}

export function useMyReports(leagueId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'report-problem'],
    queryFn: () => api.get(`/leagues/${leagueId}/report-problem`),
    enabled: !!leagueId,
    refetchInterval: 30_000,
  })
}

export function useReplyToMyReport(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, message }) => api.post(`/leagues/${leagueId}/report-problem/${reportId}/message`, { message }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'report-problem'] }),
  })
}

// Fires when the commissioner opens their report thread — server flips
// any 'replied' reports to 'open' so the Commish-tab unread badge clears.
export function useMarkMyReportsRead(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/leagues/${leagueId}/report-problem/mark-read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'report-problem'] }),
  })
}

export function useFantasyRosterForUser(leagueId, userId) {
  return useQuery({
    queryKey: ['leagues', leagueId, 'fantasy', 'roster', userId],
    queryFn: () => api.get(`/leagues/${leagueId}/fantasy/roster/${userId}`),
    enabled: !!leagueId && !!userId,
  })
}

export function useForceLineup(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, slots }) => api.post(`/leagues/${leagueId}/fantasy/rosters/${userId}/lineup`, { slots }),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster', userId] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}

export function useCommissionerAddDrop(leagueId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, add_player_id, drop_player_id }) =>
      api.post(`/leagues/${leagueId}/fantasy/rosters/${userId}/add-drop`, { add_player_id, drop_player_id }),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy', 'roster', userId] })
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'fantasy'] })
    },
  })
}
